"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { normalizeReturnTo } from "@/lib/auth/return-to";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

/** The label is also the instruction: it names the situation the press is for. */
export const SESSION_REPAIR_LABEL =
  "Починка после патча, просто сюда нажмите если раньше залогинены были";

const SESSION_REPAIR_FAILED = "Не получилось :с логиньтесь заново";

interface LoginSessionRepairProps {
  /** The raw `?callbackUrl=` this page was opened with, read back only after the guard. */
  callbackUrl: string;
}

/**
 * Asks the BFF to mint a new `accessToken` from the `refreshToken` cookie, so a
 * visitor whose access token the backend stopped accepting — a redeploy that
 * rotated the signing key invalidates every issued token at once — gets back in
 * without retyping credentials. `app/refresh/route.ts` is the same endpoint
 * `proxy.ts` uses, and it is a public path, so this press is not itself gated on
 * the session that broke. A refused refresh leaves the cookies deleted and shows
 * the login form, which is the honest answer: only credentials can continue then.
 */
export function LoginSessionRepair({
  callbackUrl,
}: Readonly<LoginSessionRepairProps>) {
  const router = useRouter();
  const [failed, setFailed] = useState(false);
  const [isPending, startTransition] = useTransition();

  const repair = () => {
    setFailed(false);

    startTransition(async () => {
      let refreshed: Response | null = null;

      try {
        refreshed = await fetch("/refresh", {
          method: "POST",
          cache: "no-store",
        });
      } catch (error) {
        console.error("Session repair request failed:", error);
      }

      if (!refreshed?.ok) {
        setFailed(true);

        return;
      }

      // The destination comes from the address, so it passes the same open-redirect
      // guard `app/auth/login/route.ts` applies to the form's hidden field.
      router.push(normalizeReturnTo(callbackUrl, window.location.origin));
      router.refresh();
    });
  };

  return (
    <>
      <Button
        variant="outline"
        size="row"
        onClick={repair}
        disabled={isPending}
      >
        {SESSION_REPAIR_LABEL}
      </Button>

      {failed && (
        <Alert variant="destructive">
          <AlertDescription>{SESSION_REPAIR_FAILED}</AlertDescription>
        </Alert>
      )}
    </>
  );
}
